import {IconType} from "react-icons";
import {
  LuChartArea,
  LuChartColumn,
  LuChartColumnStacked,
  LuChartLine,
  LuChartNoAxesColumn,
  LuChartScatter,
  LuGrid3X3,
  LuLayers,
} from "react-icons/lu";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {DatePicker} from "@/components/datePicker/DatePicker.tsx";
import {Select} from "@/components/select/Select.tsx";
import {SelectOption} from "@/components/select/Select.types.ts";
import {StandardDateRange} from "@/lib/queries/reports.ts";
import {METRIC_GRANULARITIES, MetricComparisonPeriod, MetricGranularity} from "@/lib/types/metricsExplorer.ts";
import {EXPLORER_CHART_TYPES, ExplorerChartType} from "@/pages/metricsExplorer/explorerConfig.ts";
import {ChartTypeLabel} from "@/pages/metricsExplorer/toolbar/ChartTypeLabel.tsx";
import {InlineLabelSingleValue} from "@/pages/metricsExplorer/toolbar/InlineLabelSingleValue.tsx";

import styles from "./MetricsExplorerToolbar.module.scss";

interface MetricsExplorerToolbarProps {
  start: Date;
  end: Date;
  fixedRange?: StandardDateRange;
  granularity: MetricGranularity;
  comparison: MetricComparisonPeriod | null;
  chartType: ExplorerChartType;
  onGranularityChange: (granularity: MetricGranularity) => void;
  onComparisonChange: (comparison: MetricComparisonPeriod | null) => void;
  onChartTypeChange: (chartType: ExplorerChartType) => void;
  onRangeChange: (start: Date | null, end: Date | null, fixedRange: StandardDateRange | null) => void;
}

const GRANULARITY_LABELS: Record<MetricGranularity, string> = {
  AUTO: "Auto",
  ONE_MINUTE: "1 minute",
  FIFTEEN_MINUTES: "15 minutes",
  ONE_HOUR: "1 hour",
  ONE_DAY: "1 day",
  ONE_WEEK: "1 week",
  ONE_MONTH: "1 month",
};

const COMPARISON_LABELS: Record<MetricComparisonPeriod, string> = {
  PREVIOUS_PERIOD: "Previous period",
  PREVIOUS_DAY: "Previous day",
  PREVIOUS_WEEK: "Previous week",
  PREVIOUS_MONTH: "Previous month",
  PREVIOUS_YEAR: "Previous year",
  CUSTOM_OFFSET: "Custom offset",
};

const CHART_TYPE_LABELS: Record<ExplorerChartType, string> = {
  line: "Line",
  area: "Area",
  stackedArea: "Stacked area",
  column: "Bars",
  stackedColumn: "Stacked bars",
  scatter: "Scatter (correlation)",
  distribution: "Distribution",
  heatmap: "Seasonality heatmap",
};

// Lucide, the successor to the Feather set used across the app; it has no stacked-area
// chart, so layers stands in for stacking.
const CHART_TYPE_ICONS: Record<ExplorerChartType, IconType> = {
  line: LuChartLine,
  area: LuChartArea,
  stackedArea: LuLayers,
  column: LuChartColumn,
  stackedColumn: LuChartColumnStacked,
  scatter: LuChartScatter,
  distribution: LuChartNoAxesColumn,
  heatmap: LuGrid3X3,
};

const NO_COMPARISON = "NONE";

// CUSTOM_OFFSET needs an offset field the toolbar does not have yet, so it is not offered.
const OFFERED_COMPARISONS: MetricComparisonPeriod[] = [
  "PREVIOUS_PERIOD",
  "PREVIOUS_DAY",
  "PREVIOUS_WEEK",
  "PREVIOUS_MONTH",
  "PREVIOUS_YEAR",
];

const toOption = (value: string, label: string): SelectOption => ({value, label});

export const MetricsExplorerToolbar = ({
  start,
  end,
  fixedRange,
  granularity,
  comparison,
  chartType,
  onGranularityChange,
  onComparisonChange,
  onChartTypeChange,
  onRangeChange,
}: MetricsExplorerToolbarProps) => {
  const {user} = useCurrentUserContext();

  return (
    <div className={styles.toolbar}>
      <DatePicker
        mode="range"
        selected={{from: start, to: end}}
        fixedRange={fixedRange}
        timezone={user!.account!.timezone}
        dateFormat={user!.current_date_format}
        placement="bottom-end"
        onChange={(date, selectedFixedRange) =>
          onRangeChange(date?.from ?? null, date?.to ?? null, selectedFixedRange ?? null)
        }
        disableFutureDates
        hasPopoverInput
        inputProps={{size: "large", "aria-label": "Time range"}}
      />

      <Select
        aria-label="Interval"
        size="large"
        className={styles.control}
        components={{SingleValue: InlineLabelSingleValue}}
        options={METRIC_GRANULARITIES.map((value) => toOption(value, GRANULARITY_LABELS[value]))}
        value={toOption(granularity, GRANULARITY_LABELS[granularity])}
        onChange={(option) =>
          onGranularityChange(((option as SelectOption | null)?.value ?? "AUTO") as MetricGranularity)
        }
      />

      <Select
        aria-label="Compare"
        size="large"
        className={styles.control}
        components={{SingleValue: InlineLabelSingleValue}}
        options={[
          toOption(NO_COMPARISON, "None"),
          ...OFFERED_COMPARISONS.map((value) => toOption(value, COMPARISON_LABELS[value])),
        ]}
        value={comparison ? toOption(comparison, COMPARISON_LABELS[comparison]) : toOption(NO_COMPARISON, "None")}
        onChange={(option) => {
          const value = (option as SelectOption | null)?.value ?? NO_COMPARISON;
          onComparisonChange(value === NO_COMPARISON ? null : (value as MetricComparisonPeriod));
        }}
      />

      <Select
        aria-label="Chart"
        size="large"
        className={styles.chartControl}
        formatOptionLabel={(option) => (
          <ChartTypeLabel icon={CHART_TYPE_ICONS[option.value as ExplorerChartType]} label={option.label} />
        )}
        options={EXPLORER_CHART_TYPES.map((value) => toOption(value, CHART_TYPE_LABELS[value]))}
        value={toOption(chartType, CHART_TYPE_LABELS[chartType])}
        onChange={(option) =>
          onChartTypeChange(((option as SelectOption | null)?.value ?? "line") as ExplorerChartType)
        }
      />
    </div>
  );
};
